import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { churchApi } from '../../services/api'
import toast from 'react-hot-toast'
import {
  BuildingOfficeIcon,



  CheckCircleIcon,


  DocumentArrowUpIcon,
  UserIcon,
  InformationCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline'

// Enhanced validation schema for multi-step form
const stepOneSchema = z.object({
  name: z.string().min(2, 'Church name must be at least 2 characters'),
  suffix: z.string().min(1, 'Church suffix is required (e.g., city, state abbreviation)').optional(),
  street_address: z.string().min(5, 'Street address is required'),
  city: z.string().min(2, 'City is required'),
  state: z.string().min(2, 'State is required'),
  zip_code: z.string().min(5, 'ZIP code is required'),
  country: z.string().min(2, 'Country is required').default('USA'),
  website: z.string().url('Invalid website URL').optional().or(z.literal('')),
  established_year: z.number().min(1800).max(new Date().getFullYear()).optional()
})

const stepTwoSchema = z.object({
  contact_email: z.string().email('Invalid email address'),
  contact_phone: z.string().min(10, 'Please provide a valid phone number'),
  pastor_name: z.string().min(2, 'Pastor name is required'),
  coordinator_first_name: z.string().min(2, 'Coordinator first name is required'),
  coordinator_last_name: z.string().min(2, 'Coordinator last name is required'),
  coordinator_email: z.string().email('Invalid coordinator email'),
  coordinator_phone: z.string().min(10, 'Please provide coordinator phone number')
})

const stepThreeSchema = z.object({
  description: z.string().optional(),
  denomination: z.string().min(2, 'Denomination is required'),
  weekly_attendance: z.number().min(1, 'Weekly attendance must be at least 1').optional(),
  youth_programs: z.boolean(),
  additional_notes: z.string().optional()
})

type StepOneForm = z.infer<typeof stepOneSchema>
type StepTwoForm = z.infer<typeof stepTwoSchema>
type StepThreeForm = z.infer<typeof stepThreeSchema>

type AllFormData = StepOneForm & StepTwoForm & StepThreeForm

const ChurchRegistrationPublic: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [formData, setFormData] = useState<Partial<AllFormData>>({})
  const [uploadedDocuments, setUploadedDocuments] = useState<File[]>([])

  // Step 1 Form
  const step1Form = useForm<StepOneForm>({
    resolver: zodResolver(stepOneSchema),
    defaultValues: formData
  })

  // Step 2 Form
  const step2Form = useForm<StepTwoForm>({
    resolver: zodResolver(stepTwoSchema),
    defaultValues: formData
  })

  // Step 3 Form
  const step3Form = useForm<StepThreeForm>({
    resolver: zodResolver(stepThreeSchema),
    defaultValues: formData
  })

  const handleStepOne = (data: StepOneForm) => {
    setFormData({ ...formData, ...data })
    setCurrentStep(2)
  }

  const handleStepTwo = (data: StepTwoForm) => {
    setFormData({ ...formData, ...data })
    setCurrentStep(3)
  }

  const handleStepThree = async (data: StepThreeForm) => {
    const finalData = { ...formData, ...data }
    setIsSubmitting(true)

    try {
      // Handle suffix logic: use suffix if provided, otherwise use city
      const churchSuffix = finalData.suffix?.trim() || finalData.city

      const churchData = {
        ...finalData,
        suffix: churchSuffix,
        full_name: `${finalData.name} - ${churchSuffix}`, // Create full display name
        status: 'pending',
        youth_programs: finalData.youth_programs || false,
        // Structure the address properly
        location: `${finalData.street_address}, ${finalData.city}, ${finalData.state} ${finalData.zip_code}${finalData.country !== 'USA' ? ', ' + finalData.country : ''}`
      }

      // In a real implementation, you would also upload documents here
      await churchApi.registerChurch(churchData)

      setHasSubmitted(true)
      toast.success('Church registration submitted successfully!')
    } catch (error: any) {
      console.error('Church registration failed:', error)
      toast.error(error.response?.data?.error || 'Failed to register church. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDocumentUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files) {
      const newFiles = Array.from(files)
      setUploadedDocuments([...uploadedDocuments, ...newFiles])
      toast.success(`${newFiles.length} document(s) uploaded`)
    }
  }

  const removeDocument = (index: number) => {
    const newDocs = uploadedDocuments.filter((_, i) => i !== index)
    setUploadedDocuments(newDocs)
  }

  if (hasSubmitted) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <CheckCircleIcon className="mx-auto h-16 w-16 text-green-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Registration Submitted!</h2>
          <p className="text-gray-600">
            Thank you for registering your church. Your application has been submitted and is pending approval.
          </p>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex">
              <InformationCircleIcon className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800 mb-2">What happens next?</h3>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• An administrator will review your church registration</li>
                  <li>• You'll receive an email notification when your church is approved</li>
                  <li>• Once approved, you can start registering participants for competitions</li>
                  <li>• If additional information is needed, we'll contact you</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress Steps */}
      <div className="mb-8">
        <nav aria-label="Progress">
          <ol className="flex items-center">
            {[
              { id: 1, name: 'Basic Information', icon: BuildingOfficeIcon },
              { id: 2, name: 'Contact Details', icon: UserIcon },
              { id: 3, name: 'Additional Information', icon: InformationCircleIcon }
            ].map((step, stepIdx) => (
              <li key={step.id} className={`${stepIdx !== 2 ? 'pr-8 sm:pr-20' : ''} relative`}>
                <div className="flex items-center">
                  <div
                    className={`${
                      step.id < currentStep
                        ? 'bg-blue-600'
                        : step.id === currentStep
                        ? 'bg-blue-600 border-2 border-blue-600'
                        : 'bg-gray-300'
                    } h-10 w-10 rounded-full flex items-center justify-center`}
                  >
                    <step.icon className="h-5 w-5 text-white" />
                  </div>
                  <span
                    className={`${
                      step.id <= currentStep ? 'text-blue-600' : 'text-gray-500'
                    } ml-4 text-sm font-medium`}
                  >
                    {step.name}
                  </span>
                </div>
                {stepIdx !== 2 && (
                  <div
                    className={`${
                      step.id < currentStep ? 'bg-blue-600' : 'bg-gray-300'
                    } absolute top-5 right-0 h-0.5 w-8 sm:w-20`}
                  />
                )}
              </li>
            ))}
          </ol>
        </nav>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        {/* Step 1: Basic Information */}
        {currentStep === 1 && (
          <form onSubmit={step1Form.handleSubmit(handleStepOne)} className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Basic Church Information</h2>

              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Church Name *
                  </label>
                  <input
                    {...step1Form.register('name')}
                    type="text"
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., St. Mary Coptic Orthodox Church"
                  />
                  {step1Form.formState.errors.name && (
                    <p className="mt-1 text-sm text-red-600">{step1Form.formState.errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Church Suffix (Optional)
                  </label>
                  <input
                    {...step1Form.register('suffix')}
                    type="text"
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., PA, NYC, Chicago (if empty, city will be used)"
                  />
                  {step1Form.formState.errors.suffix && (
                    <p className="mt-1 text-sm text-red-600">{step1Form.formState.errors.suffix.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Street Address *
                  </label>
                  <input
                    {...step1Form.register('street_address')}
                    type="text"
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="123 Main Street"
                  />
                  {step1Form.formState.errors.street_address && (
                    <p className="mt-1 text-sm text-red-600">{step1Form.formState.errors.street_address.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City *
                    </label>
                    <input
                      {...step1Form.register('city')}
                      type="text"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Philadelphia"
                    />
                    {step1Form.formState.errors.city && (
                      <p className="mt-1 text-sm text-red-600">{step1Form.formState.errors.city.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      State *
                    </label>
                    <input
                      {...step1Form.register('state')}
                      type="text"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="PA"
                    />
                    {step1Form.formState.errors.state && (
                      <p className="mt-1 text-sm text-red-600">{step1Form.formState.errors.state.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ZIP Code *
                    </label>
                    <input
                      {...step1Form.register('zip_code')}
                      type="text"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="19101"
                    />
                    {step1Form.formState.errors.zip_code && (
                      <p className="mt-1 text-sm text-red-600">{step1Form.formState.errors.zip_code.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country *
                  </label>
                  <input
                    {...step1Form.register('country')}
                    type="text"
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="USA"
                    defaultValue="USA"
                  />
                  {step1Form.formState.errors.country && (
                    <p className="mt-1 text-sm text-red-600">{step1Form.formState.errors.country.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Website (Optional)
                    </label>
                    <input
                      {...step1Form.register('website')}
                      type="url"
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="https://www.yourchurch.com"
                    />
                    {step1Form.formState.errors.website && (
                      <p className="mt-1 text-sm text-red-600">{step1Form.formState.errors.website.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Established Year (Optional)
                    </label>
                    <input
                      {...step1Form.register('established_year', { valueAsNumber: true })}
                      type="number"
                      min="1800"
                      max={new Date().getFullYear()}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., 1950"
                    />
                    {step1Form.formState.errors.established_year && (
                      <p className="mt-1 text-sm text-red-600">{step1Form.formState.errors.established_year.message}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Continue
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </button>
            </div>
          </form>
        )}

        {/* Step 2: Contact Information */}
        {currentStep === 2 && (
          <form onSubmit={step2Form.handleSubmit(handleStepTwo)} className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Information</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Church Contact</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contact Email *
                      </label>
                      <input
                        {...step2Form.register('contact_email')}
                        type="email"
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        placeholder="church@example.com"
                      />
                      {step2Form.formState.errors.contact_email && (
                        <p className="mt-1 text-sm text-red-600">{step2Form.formState.errors.contact_email.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contact Phone *
                      </label>
                      <input
                        {...step2Form.register('contact_phone')}
                        type="tel"
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        placeholder="(555) 123-4567"
                      />
                      {step2Form.formState.errors.contact_phone && (
                        <p className="mt-1 text-sm text-red-600">{step2Form.formState.errors.contact_phone.message}</p>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pastor/Priest Name *
                      </label>
                      <input
                        {...step2Form.register('pastor_name')}
                        type="text"
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., Fr. John Smith"
                      />
                      {step2Form.formState.errors.pastor_name && (
                        <p className="mt-1 text-sm text-red-600">{step2Form.formState.errors.pastor_name.message}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Mahragan Coordinator</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    This person will be responsible for managing your church's participation in the Mahragan.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First Name *
                      </label>
                      <input
                        {...step2Form.register('coordinator_first_name')}
                        type="text"
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                      {step2Form.formState.errors.coordinator_first_name && (
                        <p className="mt-1 text-sm text-red-600">{step2Form.formState.errors.coordinator_first_name.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name *
                      </label>
                      <input
                        {...step2Form.register('coordinator_last_name')}
                        type="text"
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                      {step2Form.formState.errors.coordinator_last_name && (
                        <p className="mt-1 text-sm text-red-600">{step2Form.formState.errors.coordinator_last_name.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        {...step2Form.register('coordinator_email')}
                        type="email"
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                      {step2Form.formState.errors.coordinator_email && (
                        <p className="mt-1 text-sm text-red-600">{step2Form.formState.errors.coordinator_email.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone *
                      </label>
                      <input
                        {...step2Form.register('coordinator_phone')}
                        type="tel"
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                      {step2Form.formState.errors.coordinator_phone && (
                        <p className="mt-1 text-sm text-red-600">{step2Form.formState.errors.coordinator_phone.message}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setCurrentStep(1)}
                className="flex items-center px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                <ArrowLeftIcon className="mr-2 h-4 w-4" />
                Back
              </button>
              <button
                type="submit"
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Continue
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </button>
            </div>
          </form>
        )}

        {/* Step 3: Additional Information & Documents */}
        {currentStep === 3 && (
          <form onSubmit={step3Form.handleSubmit(handleStepThree)} className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Additional Information</h2>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Denomination *
                  </label>
                  <select
                    {...step3Form.register('denomination')}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Denomination</option>
                    <option value="coptic_orthodox">Coptic Orthodox</option>
                    <option value="orthodox">Orthodox</option>
                    <option value="catholic">Catholic</option>
                    <option value="protestant">Protestant</option>
                    <option value="other">Other</option>
                  </select>
                  {step3Form.formState.errors.denomination && (
                    <p className="mt-1 text-sm text-red-600">{step3Form.formState.errors.denomination.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Weekly Attendance (Optional)
                  </label>
                  <input
                    {...step3Form.register('weekly_attendance', { valueAsNumber: true })}
                    type="number"
                    min="1"
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., 150"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    {...step3Form.register('youth_programs')}
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    Our church has active youth programs
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Church Description (Optional)
                  </label>
                  <textarea
                    {...step3Form.register('description')}
                    rows={4}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Brief description of your church, its history, and community..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Notes (Optional)
                  </label>
                  <textarea
                    {...step3Form.register('additional_notes')}
                    rows={3}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Any additional information you'd like to share..."
                  />
                </div>

                {/* Document Upload Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Supporting Documents (Optional)
                  </label>
                  <p className="text-sm text-gray-600 mb-4">
                    You may upload documents such as church charter, pastor credentials, or other relevant materials.
                  </p>

                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                    <div className="text-center">
                      <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="mt-4">
                        <label htmlFor="file-upload" className="cursor-pointer">
                          <span className="mt-2 block text-sm font-medium text-gray-900">
                            Upload documents
                          </span>
                          <span className="mt-1 block text-sm text-gray-600">
                            PDF, DOC, DOCX up to 10MB each
                          </span>
                          <input
                            id="file-upload"
                            name="file-upload"
                            type="file"
                            multiple
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            className="sr-only"
                            onChange={handleDocumentUpload}
                          />
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Uploaded Files List */}
                  {uploadedDocuments.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Uploaded Documents:</h4>
                      <ul className="space-y-2">
                        {uploadedDocuments.map((file, index) => (
                          <li key={index} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                            <span className="text-sm text-gray-900">{file.name}</span>
                            <button
                              type="button"
                              onClick={() => removeDocument(index)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Important Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex">
                <InformationCircleIcon className="h-5 w-5 text-blue-400 flex-shrink-0" />
                <div className="ml-3">
                  <p className="text-sm text-blue-800">
                    <strong>Please note:</strong> Your church registration will be reviewed by an administrator.
                    You'll receive an email notification once your church is approved. All information must be accurate and verifiable.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <button
                type="button"
                onClick={() => setCurrentStep(2)}
                className="flex items-center px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                <ArrowLeftIcon className="mr-2 h-4 w-4" />
                Back
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Registration'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default ChurchRegistrationPublic